import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useSignUpMutation } from '../../features/auth/authApiSlice'; // Import useSignUpMutation
import { setAuthLoading, setAuthError, selectAuthStatus, selectAuthError as selectAuthGlobalError } from '../../features/auth/authSlice'; // Also import status and error from authSlice

const RegistrationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // RTK Query mutation hook for signup
  const [signUp, { isLoading }] = useSignUpMutation(); // isLoading will be directly available from the hook
  const authStatus = useSelector(selectAuthStatus); // Global auth loading status
  const authErrorGlobal = useSelector(selectAuthGlobalError); // Global auth error message

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    errors: {}, // Local form validation errors
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      errors: { ...formData.errors, [name]: '' }, // Clear previous errors on input change
    });
    dispatch(setAuthError(null)); // Clear global auth error on input change
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required!';
      isValid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name should have more than 2 characters.';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required!';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required!';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be greater than 8 characters.';
      isValid = false;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match!';
      isValid = false;
    }

    setFormData({ ...formData, errors: newErrors });
    return isValid;
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    dispatch(setAuthError(null)); // Clear global error before new attempt

    if (!validateForm()) {
      return;
    }

    dispatch(setAuthLoading('loading')); // Set global loading status

    try {
      const response = await signUp({
        name: formData.name,
        email: formData.email,
        password: formData.password,
      }).unwrap(); // Use .unwrap() to catch errors

      console.log('Registration Response:', response);

      if (response.success) {
        dispatch(setAuthLoading('succeeded'));
        // ✅ Important: Pass user object and verifyUserToken via `state`
        // The route for verifyotp is '/user/verifyotp' based on your previous routes,
        // and its corresponding element in the router would be `verifyregistration`.
        navigate('/verifyregistration ', { 
            state: { 
                user: response.user, 
                verifyUserToken: response.verifyUserToken, // This is the JWT token used for verification
                message: response.message // Any success message you want to display
            } 
        });
      } else {
        // This block might not be hit with .unwrap(), as it will throw an error for non-success
        dispatch(setAuthError(response.message || 'Registration failed.'));
        dispatch(setAuthLoading('failed'));
      }
    } catch (err) {
      console.error('Registration Error caught in component:', err);
      // Ensure the error object is handled correctly
      const errorMessage = err.data?.message || err.error || 'An unexpected registration error occurred.'; // Changed err.message to err.error based on common RTK Query error structure
      dispatch(setAuthError(errorMessage)); // Set global auth error
      dispatch(setAuthLoading('failed'));
    }
  };

  const isSubmitting = authStatus === 'loading'; // Use global auth status for submitting state

  return (
    <div className="auth-container-new mt-5">
      <Container>
        <Row className="justify-content-md-center">
          <Col xs={12} md={4}>
            <Card className="mt-5">
              <Card.Body>
                <Card.Title className="text-center site-color-dark-red h2 mb-4">Register</Card.Title>
                <Form onSubmit={handleRegistration}>
                  <Form.Group controlId="formBasicName" className="form-group">
                    <FontAwesomeIcon icon={faUser} className="form-icon" />
                    <Form.Control type="text" placeholder="Enter your name" name="name" value={formData.name}
                      onChange={handleInputChange} disabled={isSubmitting}/>
                  </Form.Group>
                  {formData.errors.name && <span className="error">{formData.errors.name}</span>}

                  <Form.Group controlId="formBasicEmail" className="form-group">
                    <FontAwesomeIcon icon={faEnvelope} className="form-icon" />
                    <Form.Control type="email" placeholder="Enter your email" name="email" value={formData.email}
                      onChange={handleInputChange} disabled={isSubmitting}/>
                  </Form.Group>
                  {formData.errors.email && <span className="error">{formData.errors.email}</span>}

                  <Form.Group controlId="formBasicPassword" className="form-group">
                    <FontAwesomeIcon icon={faLock} className="form-icon" />
                    <Form.Control type="password" placeholder="Password" name="password" value={formData.password}
                      onChange={handleInputChange} disabled={isSubmitting}/>
                  </Form.Group>
                  {formData.errors.password && <span className="error">{formData.errors.password}</span>}

                  <Form.Group controlId="formBasicConfirmPassword" className="form-group">
                    <FontAwesomeIcon icon={faLock} className="form-icon" />
                    <Form.Control type="password" placeholder="Re-enter Password" name="confirmPassword" value={formData.confirmPassword}
                      onChange={handleInputChange} disabled={isSubmitting}/>
                  </Form.Group>
                  {formData.errors.confirmPassword && <span className="error">{formData.errors.confirmPassword}</span>}

                  <div className="form-group">
                    <Button variant="primary" type="submit" className="form-btn" disabled={isSubmitting}>
                      Sign Up {isSubmitting ? (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />) : null}
                    </Button>
                  </div>
                </Form>

                <div className="text-center mt-3">
                  <p>Already have an account? <Link to="/login">Login</Link></p>
                </div>
                {authErrorGlobal && <div className='text-center'><span className="error">{authErrorGlobal}</span></div>}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default RegistrationForm;
